import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Bell, BellOff, BellRing, ShieldAlert, Send } from 'lucide-react';
import { toast } from 'sonner';
import { ativarPushAdmin } from '../../hooks/useAdminPwa';
import { apiService } from '../../services/api';

/**
 * Card de configuração de Notificações Push (Web Push) para admins.
 *
 * Mostra status atual da permissão e expõe ações:
 *  - Ativar (chama ativarPushAdmin → pede permissão + registra SW + envia subscription)
 *  - Reativar quando bloqueado (instrui o usuário a destravar no navegador)
 *  - Enviar push de teste (chama /admin/push/test)
 *
 * Visível apenas para super_admin (sub-admins não recebem push por decisão de produto).
 */
const NotificacoesPushCard = ({ isSuperAdmin }) => {
  const [suportado, setSuportado] = useState(false);
  const [perm, setPerm] = useState('unknown');
  const [ativando, setAtivando] = useState(false);
  const [enviandoTeste, setEnviandoTeste] = useState(false);

  const ehIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const ehStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

  useEffect(() => {
    try {
      const tem = typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
      setSuportado(tem);
      setPerm(tem ? Notification.permission : 'unsupported');
    } catch {
      setSuportado(false);
      setPerm('unsupported');
    }
  }, []);

  if (!isSuperAdmin) return null;

  const handleAtivar = async () => {
    if (perm === 'denied') {
      toast.error(
        'Notificações bloqueadas no navegador. Toque no cadeado/ícone do site na barra de endereço → Permissões → Notificações → Permitir. Depois recarregue a página.',
        { duration: 8000 }
      );
      return;
    }
    setAtivando(true);
    try {
      const r = await ativarPushAdmin();
      if (r.ok) {
        setPerm('granted');
        toast.success('Notificações ativadas! Você receberá alertas de comprovantes, bloqueios e mensagens.');
      } else if (r.reason === 'permissao_negada') {
        setPerm(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
        toast.error('Permissão negada. Habilite manualmente nas configurações do navegador.');
      } else if (r.reason === 'sem_suporte') {
        toast.error('Seu navegador não suporta notificações push.');
      } else {
        toast.error(`Não foi possível ativar (${r.reason}).`);
      }
    } catch (e) {
      toast.error('Erro inesperado ao ativar notificações.');
    } finally {
      setAtivando(false);
    }
  };

  const handleEnviarTeste = async () => {
    setEnviandoTeste(true);
    const passos = [];
    const ehIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    try {
      // ============ ETAPA 1: Notification() direto (NÃO funciona em iOS PWA) ============
      passos.push(`Device: ${ehIOS ? 'iOS' : 'outro'} | UA: ${navigator.userAgent.slice(0, 60)}`);
      passos.push(`Permissão: ${Notification.permission}`);
      if (!ehIOS) {
        try {
          const n = new Notification('🧪 1 — Browser direto', {
            body: 'Notificação básica.',
            icon: '/icon-android-192.png',
          });
          setTimeout(() => { try { n.close(); } catch (_) { /* noop */ } }, 6000);
          passos.push('✅ 1 Notification() direto OK');
        } catch (e1) {
          passos.push(`❌ 1 Notification() falhou: ${e1.message}`);
        }
      } else {
        passos.push('⏭️ 1 pulado (iOS não suporta Notification() fora do SW)');
      }

      // ============ ETAPA 2: Notificação via Service Worker ============
      let regAtiva = null;
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        passos.push(`SWs registrados: ${regs.length}`);
        for (const r of regs) {
          passos.push(`  - scope=${r.scope.slice(-50)} active=${!!r.active}`);
          if (r.scope.includes('/admin') && r.active) regAtiva = r;
        }
        if (!regAtiva) {
          for (const r of regs) { if (r.active) { regAtiva = r; break; } }
        }
        if (regAtiva && regAtiva.active) {
          await regAtiva.showNotification('🧪 2 — Service Worker', {
            body: 'Se ver esta, SW e permissão OK.',
            icon: '/icon-android-192.png',
            badge: '/icon-android-192.png',
            tag: 'admin-test-sw',
            requireInteraction: false,
          });
          passos.push('✅ 2 showNotification() chamado');
        } else {
          passos.push('❌ 2 nenhum SW ativo encontrado');
        }
      } catch (e2) {
        passos.push(`❌ 2 erro: ${e2.message}`);
      }

      // ============ ETAPA 3: Push real via backend ============
      try {
        const r = await apiService.api.post('/admin/push/test', {});
        const enviados = r?.data?.enviados ?? 0;
        const falharam = r?.data?.falharam ?? 0;
        const motivo = r?.data?.motivo || 'ok';
        passos.push(`✅ 3 backend: enviados=${enviados} falharam=${falharam} motivo=${motivo}`);
      } catch (e3) {
        passos.push(`❌ 3 backend erro: ${e3.message}`);
      }

      const relatorio = passos.join('\n');
      console.log('[PUSH DIAG]\n' + relatorio);
      alert(relatorio);
    } catch (e) {
      toast.error(`Erro geral: ${e.message}`);
    } finally {
      setEnviandoTeste(false);
    }
  };

  const handleReativarInscricao = async () => {
    setAtivando(true);
    try {
      const log = [];
      // 1) Cancela inscrição atual (todas as registrations)
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        log.push(`SW encontrados: ${regs.length}`);
        for (const reg of regs) {
          try {
            const oldSub = await reg.pushManager.getSubscription();
            if (oldSub) {
              await oldSub.unsubscribe();
              log.push(`✅ unsubscribed: ${oldSub.endpoint.slice(0, 50)}...`);
            }
          } catch (_) { /* ignore */ }
          // Não desregistra o SW — só faz update
          try { await reg.update(); } catch (_) { /* ignore */ }
        }
      } catch (e) {
        log.push(`⚠️ erro limpando subs: ${e.message}`);
      }

      // 2) Limpa caches do SW pra forçar recarga
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          for (const k of keys) await caches.delete(k);
          log.push(`✅ caches limpos: ${keys.length}`);
        }
      } catch (_) { /* ignore */ }

      // 3) Re-inscreve com VAPID atual
      const r = await ativarPushAdmin();
      if (r.ok) {
        log.push('✅ Nova inscrição criada');
        toast.success('Inscrição renovada! Agora toque "Enviar push de teste".');
        console.log('[REATIVAR]\n' + log.join('\n'));
      } else {
        toast.error(`Falha ao renovar (${r.reason}). Tente fechar e reabrir o app.`);
        console.log('[REATIVAR FALHOU]\n' + log.join('\n'));
      }
    } catch (e) {
      toast.error(`Erro ao reativar: ${e.message}`);
    } finally {
      setAtivando(false);
    }
  };

  // ---- UI por estado ----
  const renderStatus = () => {
    if (!suportado || perm === 'unsupported') {
      // iOS Safari fora de PWA → mensagem específica explicando como instalar
      if (ehIOS && !ehStandalone) {
        return (
          <div className="rounded-md px-3 py-3 text-sm bg-amber-50 border border-amber-300 text-amber-800" data-testid="push-status-ios-precisa-pwa">
            <div className="flex items-start gap-2 font-semibold mb-2">
              <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>Notificações no iPhone só funcionam com o app instalado</span>
            </div>
            <ol className="list-decimal list-inside space-y-1 ml-1 text-xs leading-relaxed">
              <li>Toque no botão <strong>⎋ Compartilhar</strong> da barra do Safari (parte de baixo)</li>
              <li>Role e toque em <strong>&quot;Adicionar à Tela de Início&quot;</strong></li>
              <li>Confirme em <strong>&quot;Adicionar&quot;</strong></li>
              <li>Feche o Safari e abra o <strong>app pelo ícone</strong> que apareceu na sua tela inicial</li>
              <li>Volte aqui em <strong>Configurações → Notificações Push → Ativar notificações</strong></li>
            </ol>
            <p className="mt-2 text-xs italic">Limitação da Apple: o Safari em aba não recebe push notifications — só PWA instalado (iOS 16.4 ou superior).</p>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm" data-testid="push-status-nao-suportado">
          <ShieldAlert className="h-4 w-4" />
          <span>Este navegador não suporta notificações push. Use Chrome/Edge/Firefox no Android ou Chrome no desktop.</span>
        </div>
      );
    }
    if (perm === 'granted') {
      return (
        <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm" data-testid="push-status-ativo">
          <BellRing className="h-4 w-4" />
          <span>Notificações <strong>ATIVAS</strong> neste dispositivo. Você receberá alertas de comprovantes, bloqueios e mensagens.</span>
        </div>
      );
    }
    if (perm === 'denied') {
      return (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm" data-testid="push-status-bloqueado">
          <BellOff className="h-4 w-4" />
          <span>Notificações <strong>BLOQUEADAS</strong> no navegador. Toque no cadeado da URL → Permissões → Notificações → Permitir, e recarregue.</span>
        </div>
      );
    }
    // default
    return (
      <div className="flex items-center gap-2 text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="push-status-pendente">
        <Bell className="h-4 w-4" />
        <span>Notificações ainda <strong>não ativadas</strong> neste dispositivo. Clique em &quot;Ativar notificações&quot; abaixo.</span>
      </div>
    );
  };

  return (
    <Card data-testid="card-notificacoes-push">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Bell className="h-5 w-5 text-emerald-500" />
          <span>Notificações Push</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Receba alertas em tempo real no seu celular ou desktop quando:
        </p>
        <ul className="text-sm text-gray-700 list-disc list-inside space-y-1 ml-1">
          <li>Um cliente envia um <strong>novo comprovante de pagamento</strong></li>
          <li>Uma moto é <strong>bloqueada automaticamente</strong> (atraso de 3 dias)</li>
          <li>Um cliente <strong>envia mensagem ou abre atendimento</strong></li>
        </ul>

        <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
          💡 <strong>Dica:</strong> Para receber as notificações com o app fechado no celular, instale o app na tela de início (botão &quot;Adicionar à tela inicial&quot; do navegador) e ative as notificações por aqui.
        </div>

        {renderStatus()}

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          {perm !== 'granted' && suportado && (
            <Button
              onClick={handleAtivar}
              disabled={ativando}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-ativar-push-config"
            >
              <Bell className="h-4 w-4 mr-2" />
              {ativando ? 'Ativando...' : perm === 'denied' ? 'Como reativar' : 'Ativar notificações'}
            </Button>
          )}
          {perm === 'granted' && (
            <>
              <Button
                onClick={handleEnviarTeste}
                disabled={enviandoTeste}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="btn-enviar-push-teste"
              >
                <Send className="h-4 w-4 mr-2" />
                {enviandoTeste ? 'Enviando...' : 'Enviar push de teste'}
              </Button>
              <Button
                onClick={handleReativarInscricao}
                disabled={ativando}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="btn-reativar-push"
                title="Útil quando o push de teste não chega — força nova inscrição com VAPID atual"
              >
                <BellRing className="h-4 w-4 mr-2" />
                {ativando ? 'Renovando...' : 'Reativar inscrição'}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificacoesPushCard;
